import { BibleProjectHeaders, UNKNOWN } from "~/constants";

import { sendMessageToHostWithCompletion } from "./events";
import { isMobileNative, sleep } from "./helpers";
import { logger } from "./logging";

import type { AuthAccessTokenActionResult } from "./events";

/**
 * Adds a header to a request.
 */
export function addRequestHeader(
  init: RequestInit | undefined,
  headerName: string,
  headerValue: string,
) {
  if (!init) return;
  if (!init.headers) {
    init.headers = { [headerName]: headerValue };
  } else if (init.headers instanceof Headers) {
    init.headers.append(headerName, headerValue);
  } else if (Array.isArray(init.headers)) {
    init.headers.push([headerName, headerValue]);
  } else {
    init.headers[headerName] = headerValue;
  }
}

/**
 * Retrieves a fresh auth token value from the mobile native
 * WebView wrapper.
 */
export async function getAuthTokenFromApp() {
  if (isMobileNative()) {
    const { token } =
      await sendMessageToHostWithCompletion<AuthAccessTokenActionResult>({
        auth: { accessToken: {} },
      });

    return token;
  }

  return;
}

/**
 * Monkey patch window.fetch in order to preserve info
 * provided by the host when making client-side requests.
 */
export function patchFetch() {
  if (typeof globalThis.fetch === "function") {
    const fetch = globalThis.fetch;

    globalThis.fetch = async (input, init) => {
      try {
        if (init) {
          const { host } = new URL(
            input instanceof Request ? input.url : input,
          );

          if (host === globalThis.location.host) {
            if (isMobileNative()) {
              // Add platform headers
              addRequestHeader(
                init,
                BibleProjectHeaders.ClientPlatform,
                window.BibleProjectApp?.platform ?? UNKNOWN,
              );
              addRequestHeader(
                init,
                BibleProjectHeaders.AppVersion,
                window.BibleProjectApp?.appVersion?.toString() ?? UNKNOWN,
              );
              addRequestHeader(
                init,
                BibleProjectHeaders.WebViewVersion,
                window.BibleProjectApp?.webViewVersion?.toString() ?? UNKNOWN,
              );
            }

            // Add auth header. This is necessary for
            // client-side requests originating from the
            // mobile-native host.
            await getAuthTokenFromApp().then((token) => {
              if (token) {
                addRequestHeader(init, "Authorization", `Bearer ${token}`);

                // Update the "redirected" auth token
                // TODO: Remove this once enough mobile native
                // clients have been updated to 2.4
                const pageUrl = new URL(globalThis.location.href);

                if (pageUrl.searchParams.has("n")) {
                  pageUrl.searchParams.set("n", token);
                  history.replaceState(null, "", pageUrl);
                }
              }
            });
          }
        }
      } catch (error) {
        logger.error("Failed to patch window.fetch", error);
      }

      return fetch(input, init).catch((error) => {
        const shouldIgnore = () => {
          if (!(error instanceof DOMException)) return false;

          const ignoreList = [
            // AbortError is usually from Remix cancelling a fetcher
            "AbortError",
          ];

          return ignoreList.includes(error.name);
        };

        if (!shouldIgnore()) {
          logger.error("Fetch failed", error, { input, init });
        }

        return new Response("{}", { status: 400 });
      });
    };
  }
}

/**
 * Fetch, but retry (after a delay) if the request fails.
 */
export async function fetchWithRetry(
  url: RequestInfo | URL,
  options?: RequestInit,
  retries = 2,
  retryDelay = 1000,
): Promise<Response> {
  return new Promise((resolve, reject) => {
    fetch(url, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network error");
        }

        resolve(response);
      })
      .catch(async (error) => {
        if (retries > 0) {
          await sleep(retryDelay);

          return resolve(
            fetchWithRetry(url, options, retries - 1, retryDelay * 2),
          );
        }

        reject(error);
      });
  });
}

/**
 * Creates a queue of promises where each entry is unique based on a provided key.
 */
export function createPromiseQueue() {
  const promises = new Map<string, Promise<any>>();

  return {
    /**
     * Returns the number of items in the queue.
     */
    get size() {
      return promises.size;
    },

    /**
     * Returns a boolean indicating whether or not an item
     * with the provided key exists in the queue.
     */
    has(key: string) {
      return promises.has(key);
    },

    /**
     * Retrieves a promise from the queue.
     */
    get<T>(key: string) {
      return promises.get(key) as Promise<T>;
    },

    /**
     * Adds a promise to the queue. If a promise with the
     * same key already exists in the queue, that promise is
     * returned instead.
     */
    add<T>(key: string, promiseFactory: () => Promise<T>) {
      // Add the promise to the queue if an entry with the
      // same key doesn't already exist
      if (!promises.has(key)) {
        promises.set(
          key,
          promiseFactory().finally(() => {
            // Remove the promise from the map after it resolves/rejects
            promises.delete(key);
          }),
        );
      }

      // Return the promise from the queue
      return this.get<T>(key);
    },

    /**
     * Removes an item with the provided key from the queue.
     */
    delete(key: string) {
      promises.delete(key);
    },
  };
}
